/**
 * Portuguese Privacy Policy translations
 */
export const privacy = {
  pageTitle: "Política de Privacidade",
  heroSubtitle: "Sua privacidade é importante para nós. Esta política explica como coletamos, usamos e protegemos suas informações pessoais.",
  lastUpdated: "Última Atualização: Janeiro de 2026",
  effectiveDate: "Vigência: 1 de Janeiro de 2026",

  intro: {
    title: "Introdução",
    welcome: "Bem-vindo ao Observatório de Futuros da Juventude dos Conselhos de Desenvolvimento da JCI. Estamos comprometidos em proteger sua privacidade e garantir transparência sobre como tratamos seus dados.",
    commitment: "Esta Política de Privacidade explica quais informações pessoais coletamos, como as usamos e seus direitos em relação aos seus dados. Seguimos os princípios de minimização de dados e limitação de finalidade.",
    scope: "Esta política se aplica a todos os usuários da plataforma do Observatório de Futuros da Juventude, incluindo membros da JCI (Junior Chamber International) e visitantes públicos.",
  },

  controller: {
    title: "Controlador de Dados",
    description: "O controlador de dados responsável por suas informações pessoais é:",
    name: "Conselhos de Desenvolvimento da JCI",
    contact: "Para consultas relacionadas à privacidade, entre em contato:",
  },

  collection: {
    title: "Informações que Coletamos",
    description: "Coletamos diferentes tipos de informações dependendo de como você interage com nossa plataforma:",
    account: {
      title: "Informações da Conta",
      email: "Endereço de e-mail (para autenticação e notificações)",
      name: "Nome de exibição (opcional, para personalização)",
      avatar: "Foto de perfil (opcional, armazenada com segurança)",
      organization: "Afiliação à organização JCI (opcional)",
    },
    preferences: {
      title: "Preferências do Usuário",
      language: "Configuração de idioma preferido",
      theme: "Preferência de modo claro/escuro",
      country: "País de foco para análise de dados",
      interest: "Área de interesse principal (ex: Saúde Mental, Empreendedorismo)",
    },
    analytics: {
      title: "Dados de Análise (com consentimento)",
      description: "Quando você consente com análises, coletamos:",
      pages: "Páginas visitadas e padrões de navegação",
      scroll: "Profundidade de rolagem nas páginas de conteúdo",
      time: "Tempo gasto na plataforma",
      device: "Tipo de navegador e dispositivo (anonimizado)",
    },
  },

  usage: {
    title: "Como Usamos Seus Dados",
    description: "Usamos suas informações para os seguintes propósitos:",
    purpose1: {
      title: "Fornecimento de Serviços",
      description: "Para autenticá-lo, personalizar sua experiência e entregar os recursos do Observatório que você solicita.",
    },
    purpose2: {
      title: "Melhoria da Plataforma",
      description: "Para entender como os usuários interagem com nossa plataforma e melhorar a funcionalidade (apenas com consentimento de análise).",
    },
    purpose3: {
      title: "Comunicações",
      description: "Para enviar atualizações importantes sobre a plataforma, se você optou por receber notificações.",
    },
    purpose4: {
      title: "Segurança",
      description: "Para proteger contra acesso não autorizado e manter a integridade de nossos sistemas.",
    },
  },

  legal: {
    title: "Base Legal para Processamento",
    description: "Sob o GDPR, processamos seus dados com base nos seguintes fundamentos legais:",
    consent: {
      title: "Consentimento (Artigo 6(1)(a))",
      description: "Para rastreamento de análises e comunicações de marketing. Você pode retirar o consentimento a qualquer momento.",
    },
    contract: {
      title: "Execução de Contrato (Artigo 6(1)(b))",
      description: "Para fornecer os serviços que você solicitou ao criar uma conta.",
    },
    legitimate: {
      title: "Interesses Legítimos (Artigo 6(1)(f))",
      description: "Para medidas de segurança e prevenção de fraudes, equilibrados com seus direitos.",
    },
    obligation: {
      title: "Obrigação Legal (Artigo 6(1)(c))",
      description: "Para cumprir leis e regulamentos aplicáveis.",
    },
  },

  cookies: {
    title: "Cookies e Armazenamento Local",
    description: "Usamos cookies e armazenamento do navegador para melhorar sua experiência:",
    essential: {
      title: "Cookies Essenciais",
      description: "Necessários para autenticação e funcionalidade básica. Não podem ser desativados.",
    },
    analytics: {
      title: "Cookies de Análise (Google Analytics 4)",
      description: "Nos ajudam a entender como os visitantes usam nosso site. Ativos apenas com seu consentimento.",
    },
    preferences: {
      title: "Armazenamento de Preferências (localStorage)",
      description: "Armazena suas escolhas de idioma, tema e consentimento localmente em seu navegador.",
    },
    manage: "Você pode gerenciar suas preferências de cookies a qualquer momento em",
    manageLink: "Configurações",
  },

  thirdParty: {
    title: "Serviços de Terceiros",
    description: "Usamos os seguintes serviços de terceiros para operar nossa plataforma:",
    ga4: "Análise de site e rastreamento de uso (com consentimento)",
    lovable: "Infraestrutura de backend, autenticação e banco de dados",
    osm: "Mapas interativos mostrando a presença global da JCI",
    fonts: "Entrega de tipografia (família de fontes Plus Jakarta Sans)",
  },

  retention: {
    title: "Retenção de Dados",
    description: "Retemos seus dados apenas pelo tempo necessário para os propósitos descritos:",
    dataType: "Tipo de Dados",
    period: "Período de Retenção",
    account: "Dados da Conta",
    accountPeriod: "Até exclusão da conta ou 2 anos de inatividade",
    analytics: "Dados de Análise",
    analyticsPeriod: "14 meses (padrão do Google Analytics)",
    logs: "Logs de Erro",
    logsPeriod: "90 dias",
  },

  rights: {
    title: "Seus Direitos de Privacidade (GDPR)",
    description: "Sob o Regulamento Geral de Proteção de Dados, você tem os seguintes direitos:",
    access: {
      title: "Direito de Acesso",
      description: "Solicitar uma cópia de todos os dados pessoais que mantemos sobre você.",
    },
    rectification: {
      title: "Direito de Retificação",
      description: "Solicitar correção de dados pessoais imprecisos ou incompletos.",
    },
    erasure: {
      title: "Direito de Apagamento",
      description: "Solicitar exclusão de seus dados pessoais ('direito ao esquecimento').",
    },
    restriction: {
      title: "Direito de Restrição",
      description: "Solicitar limitação do processamento em certas circunstâncias.",
    },
    portability: {
      title: "Direito à Portabilidade de Dados",
      description: "Receber seus dados em formato estruturado e legível por máquina.",
    },
    object: {
      title: "Direito de Objeção",
      description: "Opor-se ao processamento baseado em interesses legítimos ou para marketing direto.",
    },
    exercise: "Para exercer esses direitos, visite sua",
    settingsLink: "página de Configurações",
  },

  international: {
    title: "Conformidade Internacional de Privacidade",
    description: "Como uma organização global operando em mais de 100 países, estamos comprometidos em respeitar as leis e regulamentos de privacidade locais onde quer que nossos membros estejam localizados.",
    commitment: "Embora usemos o GDPR como nosso padrão de base (pois fornece as proteções mais abrangentes), também cumprimos as leis de privacidade locais aplicáveis, incluindo:",
    ukGdpr: "UK GDPR para residentes do Reino Unido",
    lgpd: "LGPD (Lei Geral de Proteção de Dados) para residentes brasileiros",
    pipeda: "PIPEDA para residentes canadenses",
    appi: "APPI (Lei de Proteção de Informações Pessoais) para residentes japoneses",
    localRights: "Se seu país possui legislação de privacidade específica, você pode ter direitos adicionais. Entre em contato conosco para entender como suas leis locais se aplicam.",
  },

  children: {
    title: "Privacidade de Crianças",
    description: "O Observatório de Futuros da Juventude é projetado para usuários com 18 anos ou mais, de acordo com os requisitos de associação da JCI. Não coletamos conscientemente dados de crianças menores de 16 anos.",
    action: "Se você acredita que coletamos inadvertidamente dados de um menor, entre em contato imediatamente em privacy@jci.cc.",
  },

  security: {
    title: "Segurança de Dados",
    description: "Implementamos medidas de segurança robustas para proteger seus dados:",
    encryption: "Todos os dados são criptografados em trânsito (HTTPS/TLS) e em repouso",
    access: "Controles de acesso rigorosos limitam quem pode ver suas informações",
    rls: "Segurança em Nível de Linha (RLS) garante que você só pode acessar seus próprios dados",
    audit: "Auditorias de segurança regulares e avaliações de vulnerabilidade",
  },

  transfers: {
    title: "Transferências Internacionais de Dados",
    description: "Seus dados podem ser processados em países fora de sua residência, incluindo Estados Unidos e União Europeia.",
    safeguards: "Garantimos que salvaguardas apropriadas estejam em vigor, incluindo Cláusulas Contratuais Padrão (SCCs) quando exigido por lei.",
  },

  changes: {
    title: "Alterações nesta Política",
    description: "Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou requisitos legais.",
    notification: "Mudanças significativas serão comunicadas por e-mail (se você tiver uma conta) ou através de um aviso proeminente em nossa plataforma. A data de 'Última Atualização' no topo indica quando as mudanças foram feitas.",
  },

  contact: {
    title: "Fale Conosco",
    description: "Se você tiver dúvidas sobre esta Política de Privacidade ou deseja exercer seus direitos, entre em contato:",
    email: "Consultas de Privacidade:",
    dpa: "Você também tem o direito de apresentar uma reclamação à sua autoridade local de proteção de dados se acreditar que seus direitos foram violados.",
  },
} as const;
